import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import {
  ArrowRight,
  BookOpen, ShieldCheck, MessagesSquare, Cross, Car, RefreshCw,
  CalendarDays, Phone, Mail, MapPin, Globe, FileText, PhoneCall, ClipboardCheck,
} from "lucide-react";

const YELLOW = "#F5B301";

const DEFAULT_CONTENT = {
  hero_line1: "TVDE exam?",
  hero_line2: "Language barrier?",
  hero_accent: "We\u2019ve got you covered.",
  hero_subline: "Sirf exam ki tayyari nahi \u2014 real-life TVDE challenges ke liye bhi tayyar ho jayein.",
  tagline: "Aprende. Conduz. Cresce.",
  hero_stat_value: "316",
  hero_stat_label: "practice questions",
  stats: [
    { value: "316", label: "official questions" },
    { value: "3", label: "languages \u00b7 PT EN UR" },
    { value: "30", label: "question mock exams" },
  ],
  phone: "+351 912 379 799",
  email: "info@godrivetvde.com",
  website: "www.godrivetvde.com",
  address: "R. Ant\u00f3nio Gomes Soares Pereira 88, Office 1,\n4470-139 Maia, Portugal",
};

const COURSES = [
  { icon: Car, title: "First-time Training", text: "For new TVDE drivers starting their journey." },
  { icon: RefreshCw, title: "Renewal Training", text: "Refresh and renew your TVDE certificate." },
  { icon: CalendarDays, title: "Flexible Learning", text: "Choose your date & language." },
];

const LEARN = [
  { icon: BookOpen, text: "TVDE Rules & Legislation" },
  { icon: ShieldCheck, text: "Road Safety & Defensive Driving" },
  { icon: MessagesSquare, text: "Customer Service & Professional Conduct" },
  { icon: Cross, text: "First Aid, Emergency & Digital Platforms" },
];

const STEPS = [
  { icon: FileText, title: "Fill the online application form", text: "Apply for admission in under 5 minutes." },
  { icon: CalendarDays, title: "Choose your course date", text: "Pick the date and language that suit you." },
  { icon: PhoneCall, title: "Confirm your registration", text: "We contact you to confirm the details." },
  { icon: ClipboardCheck, title: "Start your training", text: "Learn for the exam — and the road ahead." },
];

const LANGS = ["Urdu", "Hindi", "Bengali", "English", "Português"];

function SectionLabel({ children, dark = false }) {
  return (
    <div className={`inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest mb-6 ${dark ? "text-white/50" : "text-muted-foreground"}`}>
      <span className="w-8 h-px" style={{ background: YELLOW }}></span>
      {children}
    </div>
  );
}

export default function Landing() {
  const nav = useNavigate();
  const [c, setC] = useState(DEFAULT_CONTENT);

  useEffect(() => {
    api.get("/content/landing").then((r) => setC({ ...DEFAULT_CONTENT, ...r.data })).catch(() => {});
  }, []);

  return (
    <Layout>
      {/* ---------- HERO (dark) ---------- */}
      <section className="bg-[#14110F] text-[#F5F1EA] grain relative overflow-hidden" data-testid="landing-hero">
        <div className="max-w-6xl mx-auto px-6 pt-20 pb-20 grid lg:grid-cols-12 gap-12 items-center relative z-10">
          <div className="lg:col-span-7 fade-in">
            <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-white/50 mb-8">
              <span className="w-8 h-px" style={{ background: YELLOW }}></span>
              {c.tagline}
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-[0.98] tracking-tight" data-testid="hero-title">
              {c.hero_line1}<br />{c.hero_line2}<br />
              <span style={{ color: YELLOW }}>{c.hero_accent}</span>
            </h1>
            <p className="mt-8 text-base md:text-lg text-white/70 max-w-xl leading-relaxed" data-testid="hero-sub">
              {c.hero_subline}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Button size="lg" onClick={() => nav("/apply")} data-testid="cta-get-started"
                className="rounded-full h-12 px-7 text-base font-semibold text-black hover:opacity-90 transition-opacity"
                style={{ background: YELLOW }}>
                Apply for admission <ArrowRight className="ml-1 w-4 h-4" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => nav("/login")} data-testid="cta-login"
                className="rounded-full h-12 px-7 text-base border-white/25 text-white bg-transparent hover:bg-white/10 hover:text-white">
                Student log in
              </Button>
            </div>
            <div className="mt-12 inline-flex items-center gap-3 border border-white/15 rounded-md px-4 py-3" data-testid="hero-badge">
              <ShieldCheck className="w-5 h-5 shrink-0" style={{ color: YELLOW }} aria-hidden="true" />
              <span className="text-sm text-white/80">Beyond the exam. Knowledge for the road ahead.</span>
            </div>
          </div>
          <div className="lg:col-span-5 slide-up hidden lg:block">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1546636889-ba9fdd63583e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxkcml2aW5nJTIwY2l0eSUyMHN0cmVldHxlbnwwfHx8fDE3ODI3MjA5NTV8MA&ixlib=rb-4.1.0&q=85&w=900"
                alt="Driving through a Portuguese city street"
                className="rounded-md border border-white/10 object-cover w-full h-[420px]"
              />
              <div className="absolute -bottom-4 -left-4 bg-[#14110F] border border-white/15 rounded-md px-4 py-3">
                <div className="font-serif text-2xl font-medium" style={{ color: YELLOW }}>{c.hero_stat_value}</div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-white/60">{c.hero_stat_label}</div>
              </div>
            </div>
          </div>
        </div>

        {/* language strip */}
        <div className="border-t border-white/10 relative z-10" data-testid="landing-languages">
          <div className="max-w-6xl mx-auto px-6 py-4 flex flex-wrap items-center gap-x-8 gap-y-2">
            <span className="text-[11px] font-mono uppercase tracking-widest text-white/40">We speak your language</span>
            {LANGS.map((l) => (
              <span key={l} className="text-sm font-mono text-white/75">{l}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- COURSES ---------- */}
      <section className="max-w-6xl mx-auto px-6 py-24" data-testid="landing-courses">
        <SectionLabel>Our courses</SectionLabel>
        <div className="grid md:grid-cols-3 gap-6 stagger">
          {COURSES.map(({ icon: Icon, title, text }) => (
            <div key={title} className="border border-border rounded-md p-8 bg-card" data-testid={`course-card-${title.toLowerCase().replace(/\s+/g, "-")}`}>
              <Icon className="w-6 h-6 mb-5" style={{ color: "#b8860b" }} aria-hidden="true" />
              <h3 className="font-serif text-base md:text-lg font-medium">{title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ---------- CURRICULUM (split) ---------- */}
      <section className="max-w-6xl mx-auto px-6 pb-24" data-testid="landing-curriculum">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionLabel>What you will learn</SectionLabel>
            <div className="space-y-0 border-t border-border">
              {LEARN.map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-4 py-5 border-b border-border">
                  <div className="w-10 h-10 rounded-full bg-[#14110F] flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5" style={{ color: YELLOW }} aria-hidden="true" />
                  </div>
                  <span className="font-medium">{text}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <img
              src="https://images.pexels.com/photos/7363095/pexels-photo-7363095.jpeg?auto=compress&w=900"
              alt="Professional driver smiling at the wheel"
              className="rounded-md border border-border object-cover w-full h-[380px]"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* ---------- PRACTICE PORTAL PROMO (dark) ---------- */}
      <section className="bg-[#14110F] text-[#F5F1EA] grain" data-testid="landing-portal-promo">
        <div className="max-w-6xl mx-auto px-6 py-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 hidden lg:block">
            <img
              src="https://images.unsplash.com/photo-1595986630530-969786b19b4d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxzdHVkZW50JTIwc3R1ZHlpbmclMjBtb2JpbGUlMjBtb2JpbGUlMjBwaG9uZXxlbnwwfHx8fDE3ODU2OTczNTd8MA&ixlib=rb-4.1.0&q=85&w=900"
              alt="Student practising TVDE questions on a phone"
              className="rounded-md border border-white/10 object-cover w-full h-[380px]"
              loading="lazy"
            />
          </div>
          <div className="lg:col-span-7">
            <SectionLabel dark>Free online practice portal</SectionLabel>
            <h2 className="font-serif text-base md:text-lg font-medium leading-snug max-w-lg">
              Every student gets full access to our exam-prep portal.
            </h2>
            <div className="mt-8 grid grid-cols-3 gap-6 max-w-md">
              {c.stats.map(({ value, label }) => (
                <div key={label} className="border-t border-white/15 pt-4">
                  <div className="font-serif text-3xl font-medium" style={{ color: YELLOW }}>{value}</div>
                  <div className="text-xs text-white/60 mt-1">{label}</div>
                </div>
              ))}
            </div>
            <p className="mt-8 text-white/70 max-w-lg leading-relaxed text-sm md:text-base">
              Study every question with audio pronunciation, illustrations and explanations —
              then test yourself with timed mock exams, anytime, on any device.
            </p>
            <Button size="lg" onClick={() => nav("/login")} data-testid="portal-login-cta"
              className="mt-8 rounded-full h-12 px-7 text-base font-semibold text-black hover:opacity-90 transition-opacity"
              style={{ background: YELLOW }}>
              Start practising <ArrowRight className="ml-1 w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* ---------- HOW TO ENROL ---------- */}
      <section className="max-w-6xl mx-auto px-6 py-24" data-testid="landing-enroll-steps">
        <SectionLabel>How to enrol</SectionLabel>
        <div className="grid md:grid-cols-4 gap-6 stagger">
          {STEPS.map(({ icon: Icon, title, text }, i) => (
            <div key={title} className="border border-border rounded-md p-6 bg-card relative">
              <div className="font-serif text-4xl font-medium mb-4" style={{ color: YELLOW }}>0{i + 1}</div>
              <Icon className="w-5 h-5 mb-3 text-muted-foreground" aria-hidden="true" />
              <h3 className="font-medium leading-snug">{title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 flex flex-wrap items-center gap-4">
          <Button size="lg" onClick={() => nav("/apply")} data-testid="enroll-apply-cta"
            className="rounded-full h-12 px-7 text-base bg-[#14110F] text-white hover:bg-[#14110F]/90">
            Fill the application form <ArrowRight className="ml-1 w-4 h-4" />
          </Button>
          <span className="text-sm text-muted-foreground">Approved students receive their portal login by email.</span>
        </div>
      </section>

      {/* ---------- CONTACT ---------- */}
      <section className="bg-[#14110F] text-[#F5F1EA] grain" data-testid="landing-footer">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-3 mb-6">
                <img src="/godrive-mark.png" alt="Go Drive TVDE" className="h-12 w-auto" />
                <div className="font-serif font-semibold text-xl">Go Drive <span style={{ color: YELLOW }}>TVDE</span></div>
              </div>
              <div className="font-serif italic text-xl" style={{ color: YELLOW }}>{c.tagline}</div>
              <p className="mt-3 text-sm text-white/60 max-w-sm leading-relaxed">
                GoDrive TVDE is your partner in professional driving education in Portugal.
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className="text-xs font-mono uppercase tracking-widest text-white/40 mb-6">Need help?</div>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5">
                <a href={`tel:${c.phone.replace(/\s+/g, "")}`} className="flex items-center gap-3 text-white/85 hover:text-white transition-colors" data-testid="contact-phone">
                  <Phone className="w-4 h-4 shrink-0" style={{ color: YELLOW }} aria-hidden="true" /> {c.phone}
                </a>
                <a href={`mailto:${c.email}`} className="flex items-center gap-3 text-white/85 hover:text-white transition-colors" data-testid="contact-email">
                  <Mail className="w-4 h-4 shrink-0" style={{ color: YELLOW }} aria-hidden="true" /> {c.email}
                </a>
                <a href={`https://${c.website.replace(/^https?:\/\//, "")}`} className="flex items-center gap-3 text-white/85 hover:text-white transition-colors" data-testid="contact-website">
                  <Globe className="w-4 h-4 shrink-0" style={{ color: YELLOW }} aria-hidden="true" /> {c.website}
                </a>
                <div className="flex items-start gap-3 text-white/85" data-testid="contact-address">
                  <MapPin className="w-4 h-4 shrink-0 mt-1" style={{ color: YELLOW }} aria-hidden="true" />
                  <span className="whitespace-pre-line">{c.address}</span>
                </div>
              </div>
              <div className="mt-10 flex flex-wrap gap-3">
                <Button onClick={() => nav("/apply")} data-testid="footer-apply-cta"
                  className="rounded-full h-11 px-6 font-semibold text-black hover:opacity-90 transition-opacity" style={{ background: YELLOW }}>
                  Apply for admission
                </Button>
                <Button variant="outline" onClick={() => nav("/login")} data-testid="footer-login-cta"
                  className="rounded-full h-11 px-6 border-white/25 text-white bg-transparent hover:bg-white/10 hover:text-white">
                  Student log in
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
